import { useState, useCallback, useRef } from 'react';
import {
  getMarketplaceModules,
  getModuleDetail,
  installModule,
  uninstallModule,
  toggleModule,
  submitReview,
  type MarketplaceModuleResponse,
  type MarketplaceReview,
} from '../services/marketplaceService';

// apiFetch throws with the raw response body as the message; pull out the API's own
// ErrorMessage/message so a 400 reads as a sentence instead of a JSON blob.
function readableError(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : '';
  try {
    const body = JSON.parse(raw);
    return body?.ErrorMessage ?? body?.errorMessage ?? body?.message ?? fallback;
  } catch {
    return raw || fallback;
  }
}

// Same event as WIDGET_INSTALLS_CHANGED_EVENT (features/widgets/widgetEvents.ts): the Widgets
// page, sidebar panel and home tiles reload when a widget is installed, paused or uninstalled
// here, instead of showing the old state until a page refresh.
const notifyWidgetInstallsChanged = () => window.dispatchEvent(new Event('widgets:installs-changed'));

export function useMarketplace() {
  const [modules, setModules] = useState<MarketplaceModuleResponse[]>([]);
  const [selectedModule, setSelectedModule] = useState<MarketplaceModuleResponse | null>(null);
  const [reviews, setReviews] = useState<MarketplaceReview[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentFilters = useRef<{ category?: string; isPremade?: boolean; search?: string }>({});

  // ── Fetch module list ──────────────────────────────────────────────────────
  const fetchModules = useCallback(
    async (category?: string, isPremade?: boolean, search?: string) => {
      currentFilters.current = { category, isPremade, search };
      setIsLoadingModules(true);
      setError(null);
      try {
        const data = await getMarketplaceModules(
          category === 'All' ? undefined : category,
          isPremade,
          search,
        );
        setModules(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load modules');
      } finally {
        setIsLoadingModules(false);
      }
    },
    [],
  );

  // Silent refetch with stored filters (called after install/uninstall/toggle)
  const refetchModules = useCallback(async () => {
    const { category, isPremade, search } = currentFilters.current;
    try {
      const data = await getMarketplaceModules(
        category === 'All' ? undefined : category,
        isPremade,
        search,
      );
      setModules(data);
    } catch (err) {
      console.error('refetch failed:', err);
    }
  }, []);

  // ── Module detail ──────────────────────────────────────────────────────────
  // Only the latest request may land: Back then quickly opening another module let the first
  // module's late response overwrite the second — its name, install state and buttons.
  const detailRequestRef = useRef(0);
  const selectModule = useCallback(async (moduleId: string) => {
    if (!moduleId) return;
    const request = ++detailRequestRef.current;
    setIsLoadingDetail(true);
    setError(null);
    try {
      const detail = await getModuleDetail(moduleId);
      if (request !== detailRequestRef.current) return;
      setSelectedModule(detail.module);
      setReviews(detail.reviews);
    } catch (err) {
      if (request !== detailRequestRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load module detail');
    } finally {
      if (request === detailRequestRef.current) setIsLoadingDetail(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    detailRequestRef.current++;
    setIsLoadingDetail(false);
    setSelectedModule(null);
    setReviews([]);
  }, []);

  // ── Install ────────────────────────────────────────────────────────────────
  const handleInstall = useCallback(
    // config/connectionValues are only meaningful for a widget-backed module — collected by
    // WidgetInstallConsent before this is called. Ignored server-side for every native module.
    // Resolves true when the install went through, so a caller (the widget consent screen) can
    // stay open and show the error instead of closing as if it had worked.
    async (moduleId: string, config?: Record<string, string>, connectionValues?: Record<string, string>): Promise<boolean> => {
      setIsProcessing(true);
      setError(null);
      try {
        const result = await installModule(moduleId, true, config, connectionValues);
        if (!result.success) {
          setError(result.errorMessage ?? 'Install failed');
          return false;
        }
        notifyWidgetInstallsChanged();
        // Get fresh state from server — no optimistic updates
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          // Update detail view too if we're on this module's detail
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
        return true;
      } catch (err) {
        setError(readableError(err, 'Install failed'));
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  // ── Uninstall ──────────────────────────────────────────────────────────────
  const handleUninstall = useCallback(
    async (moduleId: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        await uninstallModule(moduleId);
        notifyWidgetInstallsChanged();
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
      } catch (err) {
        setError(readableError(err, 'Uninstall failed'));
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  const handleToggle = useCallback(
    async (moduleId: string, isEnabled: boolean) => {
      setIsProcessing(true);
      setError(null);
      try {
        await toggleModule(moduleId, isEnabled);
        notifyWidgetInstallsChanged();
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
      } catch (err) {
        setError(readableError(err, 'Toggle failed'));
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  const handleSubmitReview = useCallback(
    async (moduleId: string, rating: number, comment: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        await submitReview(moduleId, rating, comment);
        const detail = await getModuleDetail(moduleId);
        setSelectedModule(detail.module);
        setReviews(detail.reviews);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Review failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [],
  );

  return {
    modules,
    selectedModule,
    reviews,
    isLoadingModules,
    isLoadingDetail,
    isProcessing,
    error,
    fetchModules,
    selectModule,
    clearSelection,
    handleInstall,
    handleUninstall,
    handleToggle,
    handleSubmitReview,
  };
}
